import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import type { Option } from 'fp-ts/lib/Option'
import { FeatureGroupModel, FeatureModel, PackageCardModel } from '@vp/digital-common-data-model-lib-ubik'
import { isProduction } from '@vp/digital-environment-lib-ubik'
import { PricingContextModel, usePricingContext } from '@vp/digital-pricing-lib-ubik'
import { LinkFactory, TokenReplacer } from '@vp/digital-token-replacer-lib-ubik'
import { WIX_PRIVACY_POLICY_PAGER_URL, WIX_TERMS_OF_USE_PAGER_URL } from '@vp/digital-url-service-lib-ubik'
import { analyticsDataRegistry } from '@vp/digital-tracking-lib-ubik'

import {
  BoundedContent,
  Box,
  BulletList,
  BulletListContent,
  Button,
  Column,
  Divider,
  FlexBox,
  GridContainer,
  H1,
  Icon,
  List,
  ListItem,
  P,
  Popover,
  PopoverContent,
  PopoverLauncher,
  Row,
  Spinner,
  ToggleSwitch,
  ToggleSwitchOffLabel,
  ToggleSwitchOnLabel,
  Typography,
} from '@vp/swan'
import { getWixVistaBillingUpgradePlanUrl } from '@vp/digital-url-service-lib-ubik'
import { getClientEnvironment } from '@vp/digital-environment-lib-ubik'
import { useLogger, useStyles, useIdentity } from '@vp/ubik-context'
import { useSignedInAction } from '../../common/helpers/useSignedInAction'

import {
  HERO_CTA_DETAIL,
  PLANS_HERO_CTA_DATA_SECTION_LABEL,
  trackPlansHeroCTAClick,
  trackCreateFreeVistaWixWebsitePricingClick,
  BUTTON_CLICKED_ACTION,
} from '../../common/WixNewPlansAnalytics'

import '@vp/digital-faq-lib-ubik/dist/index.css'
import { redirect } from '../../helpers/urlsHelper'
import { FREE_PACKAGE, isNewPlan } from '../../helpers/packageTierMapper'
import WixNewPackageGridHeader from '../../common/wixNewPackageGrid/WixNewPackageGridHeader'
import WixNewPackageGrid from '../../common/wixNewPackageGrid/WixNewPackageGrid'
import WebsitesFAQs from '../../common/WebsitesFAQs/WebsitesFAQs'
import PaymentsControlContainer from '../../common/newPaymentsControlContainer/PaymentsControlContainer'
import SalesBanner from '../../common/salesBanner/SalesBanner'

import styles, { stylesheet } from './WixNewAnonymousPlansPage.scss'
import { SubscriptionOptionsEnum, WixPageAnalyticsContext } from '../../common/helpers/WixPageAnalyticsProvider'
import { getPartnerAccountSiteIds } from '../../common/helpers/partnerAccountHelpers'
import BackBtn from '../../common/components/BackBtn'
import { usePlansPricing } from '../../common/hooks/usePlansPricing/usePlansPricing'
import useSessionPlan from '../../common/hooks/useSessionPlan/useSessionPlan'

const WixNewAnonymousPlansPage = (pageContext: any) => {
  const logger = useLogger()
  useStyles(stylesheet)
  const { setSelectedPlan } = useSessionPlan()
  const [isShorterJourneyEnabled, setIsShorterJourneyEnabled] = useState(false)

  const [siteId, setSiteId] = useState('')
  const { wixPlans } = analyticsDataRegistry
  const { trackProductList, trackToggleClickedEvent, trackPlanChosen } = useContext(WixPageAnalyticsContext)
  const {
    mpv: { version: productVersion },
    urlWixPartnerGetStartedTnCAcceptedFromPlansUrls,
    hero,
    salesBanner,
    billedYearlyToggle,
    payments,
    accessibility,
    comparePlans,
    faqs,
    plansContent,
    isDomainSearchEnabled,
    domainsModalSearch,
  } = pageContext
  const [plansCtasIndex, setPlansCtasIndex] = useState<number[]>([])

  const content = {
    faqs,
    packageGridPlans: {
      packages: [...plansContent.packageCardContainer.packageCards.packages].filter((packageCard: any) => packageCard.key !== FREE_PACKAGE).reverse(),
    },
  }

  const [processedFaqsData, setProcessedFaqsData] = useState<FeatureGroupModel | null>(null)

  const urlWixPartnerGetStartedTnCAcceptedUrl = isProduction()
    ? urlWixPartnerGetStartedTnCAcceptedFromPlansUrls.prod
    : urlWixPartnerGetStartedTnCAcceptedFromPlansUrls.qa

  const [isGetStartedLoading, setIsGetStartedLoading] = useState(false)
  const [loadingPlans, setLoadingPlans] = useState([''])
  const [isBilledYearly, setIsBilledYearly] = useState(true)
  const [isClient, setIsClient] = useState(false)
  const [isOverrideSales, setIsOverrideSales] = useState(false)

  const plansFeaturesSectionRef = useRef(null)
  const [isLoadingPartnerAccount, setIsLoadingPartnerAccount] = useState(true)
  const { auth, isSignedIn } = useIdentity()
  const canonicalId = auth?.getCanonicalId()

  const isMobile = false

  const pricingContext: Option<PricingContextModel> = usePricingContext()
  usePlansPricing({
    trackProductList,
    analyticsPage: wixPlans,
    locale: pageContext.mpv.locale,
    pricingContext,
  })

  // Initialize client-side state
  useEffect(() => {
    setIsClient(true)

    if (typeof window !== 'undefined') {
      // Handle override params for testing
      if (!isProduction() && window.location?.search) {
        const queryString = window.location.search
        const urlParams = new URLSearchParams(queryString)
        setIsOverrideSales(urlParams?.get('sales') === 'true' && isBilledYearly)
      }
    }
  }, [isBilledYearly])

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setLoadingPlans([])
        setIsGetStartedLoading(false)
      }
    }

    window.addEventListener('pageshow', handlePageShow)

    return () => {
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  const addPlanToLoadingPlans = (planKey?: string) => {
    if (planKey) {
      const newLoadingPlans = new Set([...loadingPlans])
      newLoadingPlans.add(planKey)
      setLoadingPlans(Array.from(newLoadingPlans))
      setIsGetStartedLoading(isGetStartedLoading || planKey == FREE_PACKAGE)
    }
  }

  const removePlanFromLoadingPlans = (planKey?: string) => {
    if (planKey) {
      const newLoadingPlans = loadingPlans.filter((plan: string) => plan !== planKey)
      setLoadingPlans(newLoadingPlans)
      setIsGetStartedLoading(isGetStartedLoading && !(planKey == FREE_PACKAGE))
    }
  }

  const handleCompareTableScroll = () => {
    if (plansFeaturesSectionRef.current)
      (plansFeaturesSectionRef.current as any).scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
  }

  const handleWixGetStartedNavigation = async () => {
    try {
      logger.debug('Clicking Wix Anonymous Plans Page Get Started to Wix GetStarted Navigate')
      let url = urlWixPartnerGetStartedTnCAcceptedUrl
      if (isMobile) {
        url += '&deviceType=mobile'
      }
      addPlanToLoadingPlans(FREE_PACKAGE)
      await redirect(url)
    } catch (error: any) {
      removePlanFromLoadingPlans(FREE_PACKAGE)
      logger.error('Error handling Get Started Navigation:', error)
    }
  }

  const handleChoosePlanNavigation = async (planKey: string) => {
    try {
      addPlanToLoadingPlans(planKey)

      window.location.href = getWixVistaBillingUpgradePlanUrl(
        pageContext.locale,
        siteId,
        'anonymous-plan-picker-page',
        getClientEnvironment(),
        'anonymous-plan-picker-page',
      )
    } catch (error: any) {
      removePlanFromLoadingPlans(planKey)
      logger.error('Error handling Choose Plan Navigation:', error)
    }
  }

  const fetchPartnerAccount = useCallback(async (locale: string) => {
    try {
      const data = await getPartnerAccountSiteIds(locale, logger, 'anonymous-plan-picker-page')
      const websitesCount = data?.length ?? 0
      if (websitesCount == 0) {
        setIsShorterJourneyEnabled(false)
        setPlansCtasIndex([])
        setIsLoadingPartnerAccount(false)
        return
      }
      if (websitesCount === 1) {
        setSiteId(data[0])
      }
      setIsShorterJourneyEnabled(true)
      setPlansCtasIndex(
        content.packageGridPlans.packages.map((plan: PackageCardModel) => {
          if (plan.key === FREE_PACKAGE) return 2
          return 0
        }),
      )
    } catch (error) {
      logger.warn('Failed to fetch partner account group:', error)
      setIsShorterJourneyEnabled(false)
      setPlansCtasIndex([])
    } finally {
      setIsLoadingPartnerAccount(false)
    }
  }, [])

  const [, , initiateFetchPartnerAccount] = useSignedInAction<string, void>(fetchPartnerAccount, 'fetch-partner-account-after-sign-in')

  useEffect(() => {
    if (!canonicalId) return
    if (!isSignedIn) {
      setIsShorterJourneyEnabled(false)
      setPlansCtasIndex([])
      setIsLoadingPartnerAccount(false)
      return
    }
    initiateFetchPartnerAccount(pageContext.locale)
  }, [canonicalId, isSignedIn, pageContext.locale, initiateFetchPartnerAccount])

  const refactoredPackageGridPlans = {
    ...content.packageGridPlans,
    packages: content.packageGridPlans.packages.map((plan: any) => ({
      ...plan,
      isLoading: isLoadingPartnerAccount && plan.key !== FREE_PACKAGE,
      selectPackageButtons: plan.selectPackageButtons.map((button: any, index: any) => ({
        ...button,
        isLoading: isLoadingPartnerAccount && plan.key !== FREE_PACKAGE && index === 0,
      })),
    })),
  }

  const onPackageSelected = (plan: PackageCardModel) => {
    if (!plan.key) {
      logger.error('Plan key is undefined. Cannot proceed with package selection.')
      return
    }
    // add navigation event
    if (plan.key === FREE_PACKAGE) {
      trackCreateFreeVistaWixWebsitePricingClick(urlWixPartnerGetStartedTnCAcceptedUrl)
      handleWixGetStartedNavigation()
    } else {
      setSelectedPlan(plan)
      if (!wixPlans) {
        logger.error('Wix Plans analytics data is undefined. Cannot track plan chosen event.')
      } else {
        trackPlanChosen(wixPlans, plan.key, undefined, BUTTON_CLICKED_ACTION)
      }
      handleChoosePlanNavigation(plan.key)
    }
  }

  useEffect(() => {
    // Ensure the necessary data exists before processing
    if (!faqs?.features) {
      logger.debug('FAQ processing skipped, required data missing.')
      setProcessedFaqsData(null) // Reset if data becomes unavailable
      return
    }

    logger.debug('Processing FAQ data...')
    // Create the new data structure
    const newFaqsData = {
      ...faqs, // Spread original FAQ structure
      features: faqs.features.map((feature: any) => ({
        title: feature.name,
        description: <TokenReplacer template={feature.description} convertMarkdown replacements={{}} />,
      })),
    }
    // Set the processed data into state
    setProcessedFaqsData(newFaqsData)
    logger.debug('Processed FAQ data set in state:', newFaqsData)

    // Add dependencies: Re-run effect if any of these change
  }, [faqs])

  return (
    <>
      <BoundedContent>
        {/* back button */}
        {hero?.actions?.[0]?.name && isClient && (
          <Box display={{ xs: 'none', sm: 'none', md: 'block' }}>
            <BackBtn ctaName={hero.actions[0].name} className={styles.wixNewPlansPageBackButton} />
          </Box>
        )}

        <GridContainer component="section" mt={7}>
          {/* hero */}
          {hero?.title && hero?.subTitle && (
            <Row>
              <Column span={12}>
                <Box className={styles.wixNewPlansPageHeroContainer}>
                  <H1 fontSize="x3large">{hero.title}</H1>

                  <P fontSize="large" fontWeight="bold" mb={0}>
                    {hero.subTitle}
                  </P>

                  <Button
                    mt={5}
                    skin="primary"
                    onClick={() => {
                      trackPlansHeroCTAClick(urlWixPartnerGetStartedTnCAcceptedUrl)
                      handleWixGetStartedNavigation()
                    }}
                    data-section={PLANS_HERO_CTA_DATA_SECTION_LABEL}
                    data-position="1"
                    data-translation={HERO_CTA_DETAIL}
                  >
                    {hero.actions?.[1]?.name}

                    {isGetStartedLoading && <Spinner accessibleText="Loading..." />}
                  </Button>

                  <List skin="minimal" mt={5} p={0} className={styles.wixNewPlansPageHeroFeatures}>
                    {hero.heroFeatures?.features.map((item: FeatureModel) => (
                      <ListItem key={item.description} fontSize="xsmall" fontWeight="bold" mx="3" p={0}>
                        <Icon iconType="check" size="20p" skin="standard" mr="2" />
                        {item.description}
                      </ListItem>
                    ))}
                  </List>

                  <P mt={5} mb={0} textColor="standard" fontSize="xsmall">
                    <TokenReplacer
                      template={hero.actions?.[1]?.description || ''}
                      replacements={{
                        link: LinkFactory({
                          wixTermsOfUsePagerUrl: WIX_TERMS_OF_USE_PAGER_URL,
                          wixPrivacyPolicyPagerUrl: WIX_PRIVACY_POLICY_PAGER_URL,
                        }),
                      }}
                      convertMarkdown
                    />
                  </P>
                </Box>
              </Column>
            </Row>
          )}

          {/* divider */}

          <Row mt={7}>
            <Column span={12}>
              <Box display={{ xs: 'none', sm: 'block' }}>
                <Divider />
              </Box>
            </Column>
          </Row>
        </GridContainer>
      </BoundedContent>

      <SalesBanner content={salesBanner} />

      <BoundedContent mt={5}>
        <GridContainer component="section">
          {/* billing cycle toggle */}
          {billedYearlyToggle && (
            <Row>
              <Column span={12}>
                <Box component="section" textAlign="right">
                  <ToggleSwitch
                    mr={0}
                    activated={isBilledYearly}
                    onRequestActivatedChange={() => {
                      setIsBilledYearly(!isBilledYearly)
                      trackToggleClickedEvent(wixPlans, isBilledYearly ? SubscriptionOptionsEnum.MONTHLY : SubscriptionOptionsEnum.ANNUALLY)
                    }}
                  >
                    <ToggleSwitchOnLabel>{billedYearlyToggle.title}</ToggleSwitchOnLabel>
                    <ToggleSwitchOffLabel textColor="subtle">{billedYearlyToggle.title}</ToggleSwitchOffLabel>
                  </ToggleSwitch>

                  <Typography fontSkin="footnote-bold" textColor="promo" mt={3}>
                    {isBilledYearly ? (
                      billedYearlyToggle.subTitle
                    ) : (
                      <Popover className={styles.wixNewPlansPageBillingTogglePopover}>
                        <PopoverLauncher>
                          <Typography textColor="standard" fontSize="xsmall" component="span" fontWeight="bold">
                            {billedYearlyToggle.description}{' '}
                            <Typography tabIndex={0} component="span" textColor="error" className={styles.wixNewPlansPageBillingToggleDescriptionError}>
                              {billedYearlyToggle.footerDescription}
                            </Typography>
                          </Typography>
                        </PopoverLauncher>
                        <PopoverContent className={styles.wixNewPlansPageBillingTogglePopoverContent} darkMode>
                          <Typography component="p" fontSize="small" m={0}>
                            {billedYearlyToggle.heroFeatures.title}
                          </Typography>
                          <Typography component="p" fontSize="small" m={0} fontWeight="bold">
                            {billedYearlyToggle.heroFeatures.description}
                          </Typography>
                          <BulletList mt={5}>
                            {billedYearlyToggle.heroFeatures.features.map((feature: any) => (
                              <ListItem key={feature.name} p={0} mb="2">
                                <BulletListContent component="p" className="text-size-6" fontSize="small" m={0}>
                                  <Icon iconType="toggleOff" mr="3" size="16p" />
                                  {feature.name}
                                </BulletListContent>
                              </ListItem>
                            ))}
                          </BulletList>
                        </PopoverContent>
                      </Popover>
                    )}
                  </Typography>
                </Box>
              </Column>
            </Row>
          )}

          {/* plans header */}
          {content.packageGridPlans && (
            <Row mt={5}>
              <Column span={12}>
                <WixNewPackageGridHeader
                  packageGridPlans={refactoredPackageGridPlans}
                  plansCtasIndex={plansCtasIndex}
                  selectPackageCallback={onPackageSelected}
                  defaultSkin={isShorterJourneyEnabled ? 'primary' : 'secondary'}
                  buttonsPosition={'top'}
                  productVersion={productVersion}
                  highlightedPlans={['Business']}
                  isBilledYearly={isBilledYearly}
                  showDomainAvailability={true}
                  isDomainSearchEnabled={isDomainSearchEnabled}
                  domainsModalSearch={domainsModalSearch}
                  pageAnalyticsData={wixPlans}
                  overrideSales={isClient && isOverrideSales}
                  accessibility={{
                    translations: accessibility,
                  }}
                  loadingPlans={loadingPlans}
                />
              </Column>
            </Row>
          )}

          {/* compare plan features cta */}
          {comparePlans && (
            <Row>
              <Column span={12}>
                <FlexBox justifyContent="center">
                  <Button m={0} skin="secondary" onClick={handleCompareTableScroll} size={isMobile ? 'mini' : 'standard'}>
                    {comparePlans.name}
                  </Button>
                </FlexBox>
              </Column>
            </Row>
          )}

          {/* payments methods */}
          {payments && (
            <Row mt={7}>
              <Column span={12}>
                <PaymentsControlContainer content={payments} />
              </Column>
            </Row>
          )}
        </GridContainer>

        {/* plans features */}
        {content.packageGridPlans && (
          <GridContainer component="section" ref={plansFeaturesSectionRef} mt={7}>
            <Row>
              <Column span={12}>
                <Divider />

                <WixNewPackageGrid
                  packageGridPlans={{
                    ...content.packageGridPlans,
                    packages: content.packageGridPlans.packages.filter((plan: PackageCardModel) => isNewPlan(plan.key ?? '')),
                  }}
                  productVersion={productVersion}
                  highlightedPlans={['Business']}
                  isBilledYearly={isBilledYearly}
                  overrideSales={isClient && isOverrideSales}
                />
              </Column>
            </Row>
          </GridContainer>
        )}
      </BoundedContent>

      {/* faq */}
      {processedFaqsData && (
        <BoundedContent as="article" mt={{ xs: 8, sm: 9, md: 9, lg: 10, xl: 10 }} py={{ xs: 8, sm: 9, md: 9, lg: 10, xl: 10 }} backgroundColor="warm-white">
          <WebsitesFAQs content={processedFaqsData} collapsed />
        </BoundedContent>
      )}
    </>
  )
}

export default WixNewAnonymousPlansPage
